import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, Pencil, Trash2, TrendingUp, Calendar } from "lucide-react";
import { useInvestmentAccountDetail } from "@/hooks/useInvestmentAccountDetail";
import { useInvestmentTransactions, useCreateInvestmentTransaction } from "@/hooks/useInvestmentTransactions";
import { useInvestmentMetrics } from "@/hooks/useInvestmentMetrics";
import { InvestmentMetricsCards } from "@/components/investment-metrics/InvestmentMetricsCards";
import { InvestmentTransactionList } from "@/components/investment-transaction-list/InvestmentTransactionList";
import { ErrorFallback } from "@/components/ErrorFallback";
import { InvestmentTransactionForm } from "@/components/investment-transaction-form/InvestmentTransactionForm";
import { InvestmentForm } from "@/components/investment-form/InvestmentForm";
import { useInvestments, useUpdateInvestment, useDeleteInvestment } from "@/hooks/useInvestments";
import { useInvestmentTypes } from "@/hooks/useInvestmentTypes";
import { useFetchAccounts } from "@accountHooks";
import { useRiskRatings } from "@/hooks/useRiskRatings";
import { RiskRatingBadge } from "@/components/risk-rating-badge/RiskRatingBadge";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { InvestmentTransactionData } from "@/types/investments";
import type { InvestmentFormData } from "@/hooks/useInvestments";

export default function InvestmentAccountDetail() {
  const { investmentId } = useParams<{ investmentId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch investment account data
  const { data: account, isLoading, error } = useInvestmentAccountDetail(investmentId || '');

  // Fetch investment transactions
  const { data: transactions, isLoading: transactionsLoading } = useInvestmentTransactions(investmentId || '');

  // Calculate investment metrics
  const { metrics, isLoading: metricsLoading } = useInvestmentMetrics(account, transactions);

  // Fetch all investments and accounts for the transaction form
  const { data: investments = [] } = useInvestments();
  const { data: accounts = [] } = useFetchAccounts();
  
  // Fetch investment types for the edit form
  const { data: investmentTypes = [] } = useInvestmentTypes();

  // Fetch risk ratings for the investment form
  const { data: riskRatings = [], isError: riskRatingsError } = useRiskRatings();

  // Mutation hook for creating transactions
  const createTransaction = useCreateInvestmentTransaction();
  
  // Mutation hook for updating investment account
  const updateInvestment = useUpdateInvestment();
  
  // Mutation hook for deleting investment account
  const deleteInvestment = useDeleteInvestment();

  // Dialog state management
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isTransactionDialogOpen, setIsTransactionDialogOpen] = useState(false);
  const [transactionType, setTransactionType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [deletingAccountId, setDeletingAccountId] = useState<string | null>(null);

  const handleBack = () => {
    navigate("/investments");
  };

  const handleAddTransaction = (type: 'deposit' | 'withdrawal') => {
    setTransactionType(type);
    setIsTransactionDialogOpen(true);
  };

  const handleEditAccount = () => {
    setIsEditDialogOpen(true);
  };

  const handleDeleteAccount = () => {
    if (investmentId) {
      setDeletingAccountId(investmentId);
    }
  };

  const handleTransactionSave = async (data: InvestmentTransactionData) => {
    try {
      await createTransaction.mutateAsync(data);
      
      toast({
        title: "Success",
        description: `Transaction ${data.transaction_type === 'deposit' ? 'deposited' : 'withdrawn'} successfully.`,
      });
      
      setIsTransactionDialogOpen(false);
    } catch (error) {
      console.error('Error creating transaction:', error);
      
      // Determine if it's a network error
      const isNetworkError = error instanceof Error && 
        (error.message.includes('network') || 
         error.message.includes('fetch') || 
         error.message.includes('timeout') ||
         error.message.includes('Failed to fetch'));
      
      // Keep form open with data preserved on error
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create transaction. Please try again.",
        variant: "destructive",
        action: isNetworkError ? {
          label: "Retry",
          onClick: () => handleTransactionSave(data),
        } : undefined,
      });
      
      // Form stays open so user can retry or modify data
    }
  };

  const handleTransactionCancel = () => {
    setIsTransactionDialogOpen(false);
  };
  
  const handleAccountUpdate = async (data: InvestmentFormData) => {
    if (!investmentId) return;
    
    try {
      await updateInvestment.mutateAsync({
        id: investmentId,
        ...data,
      });
      
      toast({
        title: "Success",
        description: "Investment account updated successfully.",
      });
      
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating investment account:', error);
      
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update investment account. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const handleEditCancel = () => {
    setIsEditDialogOpen(false);
  };
  
  const handleDeleteConfirm = async () => {
    if (!deletingAccountId) return;
    
    try {
      await deleteInvestment.mutateAsync(deletingAccountId);
      
      toast({
        title: "Success",
        description: "Investment account deleted successfully.",
      });
      
      // Navigate to investments list on success
      navigate("/investments");
    } catch (error) {
      console.error('Error deleting investment account:', error);
      
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete investment account. Please try again.",
        variant: "destructive",
      });
      
      // Close dialog on error
      setDeletingAccountId(null);
    }
  };
  
  const handleDeleteCancel = () => {
    setDeletingAccountId(null);
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Handle error state - account not found
  if (error) {
    return (
      <ErrorBoundary>
        <div className="container mx-auto p-6">
          <ErrorFallback
            error={error as Error}
            resetError={() => navigate("/investments")}
            title="Account Not Found"
            description="The investment account you're looking for doesn't exist or you don't have access to it. Click below to return to the investments list."
            showHomeButton={false}
          />
        </div>
      </ErrorBoundary>
    );
  }
  
  // Handle case where account data is null (shouldn't happen with error handling above, but defensive)
  if (!isLoading && !account) {
    return (
      <ErrorBoundary>
        <div className="container mx-auto p-6">
          <ErrorFallback
            resetError={() => navigate("/investments")}
            title="Account Not Found"
            description="The investment account you're looking for doesn't exist. Click below to return to the investments list."
            showHomeButton={false}
          />
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="container mx-auto p-4 md:p-6 space-y-6">
        {/* Back Button and Action Buttons */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-2 min-h-[44px] min-w-[44px]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Investments
          </Button>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleAddTransaction('deposit')}
              disabled={isLoading}
              className="min-h-[44px] min-w-[44px]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Transaction
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleEditAccount}
              disabled={isLoading}
              className="min-h-[44px] min-w-[44px]"
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit Account
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteAccount}
              disabled={isLoading}
              className="min-h-[44px] min-w-[44px]"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Account
            </Button>
          </div>
        </div>

        {/* Account Summary Section */}
        <Card>
          <CardHeader>
            <CardTitle>Account Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-8 w-3/4" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-6 w-1/2" />
                  <Skeleton className="h-5 w-16" />
                </div>
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-6 w-1/3" />
              </div>
            ) : account ? (
              <div className="space-y-6">
                {/* Account Name and Type */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-2">{account.fund_name}</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">
                      {account.investment_type?.name || 'Investment'}
                    </Badge>
                    <RiskRatingBadge ratingName={account.risk_rating?.name} />
                    <span className="text-sm text-muted-foreground">
                      {account.fund_manager}
                    </span>
                  </div>
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Current Balance */}
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Current Balance</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(account.balance, account.currency)}
                    </p>
                  </div>

                  {/* Currency */}
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Currency</p>
                    <p className="text-lg font-semibold">{account.currency}</p>
                  </div>

                  {/* Interest Rate */}
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Interest Rate</p>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      <p className="text-lg font-semibold">{account.interest_rate}%</p>
                    </div>
                  </div>

                  {/* Creation Date */}
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Created</p>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm font-medium">{formatDate(account.created_at)}</p>
                    </div>
                  </div>
                </div>

                {/* Account Details */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Account Number</p>
                      <p className="font-mono text-sm">{account.account_number}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Starting Amount</p>
                      <p className="font-semibold">
                        {formatCurrency(account.starting_amount, account.currency)}
                      </p>
                    </div>
                    {account.member_number && (
                      <div>
                        <p className="text-sm text-muted-foreground">Member Number</p>
                        <p className="font-mono text-sm">{account.member_number}</p>
                      </div>
                    )}
                  </div>

                  {/* Conditional Description Display */}
                  {account.description && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Description</p>
                      <p className="text-sm">{account.description}</p>
                    </div>
                  )}

                  {/* Notes (if present) */}
                  {account.notes && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Notes</p>
                      <p className="text-sm text-muted-foreground italic">{account.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {/* Metrics Section */}
        <InvestmentMetricsCards
          metrics={metrics}
          currency={account?.currency || 'USD'}
          isLoading={metricsLoading}
        />

        {/* Transaction History Section */}
        <Card>
          <CardHeader>
            <CardTitle>Transaction History</CardTitle>
          </CardHeader>
          <CardContent>
            <InvestmentTransactionList
              transactions={transactions || []}
              isLoading={transactionsLoading}
              currency={account?.currency || 'USD'}
              onTransactionClick={(t) => navigate('/transactions/investment/' + t.id)}
            />
          </CardContent>
        </Card>

        {/* Dialogs */}
        
        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingAccountId} onOpenChange={(open) => !open && handleDeleteCancel()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Investment Account</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{account?.fund_name}</strong>? This action cannot be undone. All transactions associated with this account will also be deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleDeleteCancel}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleteInvestment.isPending}
              >
                {deleteInvestment.isPending ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Edit Account Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Investment Account</DialogTitle>
              <DialogDescription>
                Update the details of your investment account.
              </DialogDescription>
            </DialogHeader>
            <InvestmentForm
              investment={account}
              investmentTypes={investmentTypes}
              riskRatings={riskRatings}
              riskRatingsError={riskRatingsError}
              onSave={handleAccountUpdate}
              onCancel={handleEditCancel}
              isSubmitting={updateInvestment.isPending}
            />
          </DialogContent>
        </Dialog>
        
        {/* Add Transaction Dialog */}
        <Dialog open={isTransactionDialogOpen} onOpenChange={setIsTransactionDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {transactionType === 'deposit' ? 'Add Deposit' : 'Add Withdrawal'}
              </DialogTitle>
              <DialogDescription>
                {transactionType === 'deposit' 
                  ? 'Add a deposit transaction to this investment account.'
                  : 'Add a withdrawal transaction from this investment account.'}
              </DialogDescription>
            </DialogHeader>
            <InvestmentTransactionForm
              transactionType={transactionType}
              investments={investments}
              accounts={accounts}
              onSave={handleTransactionSave}
              onCancel={handleTransactionCancel}
              isSubmitting={createTransaction.isPending}
              defaultInvestmentId={investmentId}
            />
          </DialogContent>
        </Dialog>
      </div>
    </ErrorBoundary>
  );
}
